import { useEffect, useState } from "react";
import { Briefcase, GraduationCap, Inbox, RefreshCw, Search, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi, errText, fmtDate } from "./api";

const TABS = [
  { key: "consultations", label: "Consultations", icon: Inbox },
  { key: "careers", label: "Career Applications", icon: Briefcase },
  { key: "internships", label: "Internships", icon: GraduationCap },
];

const STATUSES = [["new", "New"], ["contacted", "Contacted"], ["closed", "Closed"]];

const FIELD_MAP = {
  consultations: [["organization", "Organization"], ["requirement_type", "Area"], ["message", "Message"], ["preferred_contact", "Preferred contact"]],
  careers: [["position", "Position"], ["experience", "Experience"], ["linkedin", "LinkedIn"], ["portfolio", "Portfolio"], ["message", "Message"]],
  internships: [["institution", "Institution"], ["field_of_interest", "Field"], ["duration", "Duration"], ["availability", "Availability"], ["motivation", "Motivation"]],
};

export default function Submissions() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("consultations");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const load = () => adminApi.get("/admin/submissions").then((r) => setData(r.data)).catch(() => toast.error("Could not load submissions."));
  useEffect(() => { load(); }, []);

  const setStatus = async (kind, id, status) => {
    try {
      await adminApi.patch(`/admin/submissions/${kind}/${id}/status`, { status });
      setData((d) => ({ ...d, [kind]: d[kind].map((r) => (r.id === id ? { ...r, status } : r)) }));
    } catch (err) {
      toast.error(errText(err));
      load();
    }
  };

  const remove = async (kind, r) => {
    if (!window.confirm(`Are you sure you want to delete the submission from ${r.name}? This action cannot be undone.`)) return;
    try {
      await adminApi.delete(`/admin/submissions/${kind}/${r.id}`);
      setData((d) => ({ ...d, [kind]: d[kind].filter((x) => x.id !== r.id) }));
      toast.success("Submission deleted.");
    } catch (err) {
      toast.error(errText(err));
    }
  };

  if (!data) return <p className="admin-loading" data-testid="admin-loading">Loading submissions…</p>;

  const rows = (data[tab] || []).filter((r) => {
    const s = r.status || "new";
    if (statusFilter !== "all" && s !== statusFilter) return false;
    if (!query) return true;
    const q = query.toLowerCase();
    return (r.name || "").toLowerCase().includes(q) || (r.email || "").toLowerCase().includes(q);
  });

  return (
    <div data-testid="admin-dashboard-submissions">
      <div className="admin-page-head admin-head">
        <div>
          <h1>Contact Submissions</h1>
          <p>Every enquiry and application, stored with its AI brief and status.</p>
        </div>
        <button className="btn btn-secondary" onClick={load} data-testid="admin-refresh-button"><RefreshCw size={15} /> Refresh</button>
      </div>
      <div className="admin-stats">
        {TABS.map((t) => (
          <button key={t.key} className={`admin-stat${tab === t.key ? " active" : ""}`} onClick={() => setTab(t.key)} data-testid={`admin-tab-${t.key}`}>
            <t.icon size={19} />
            <strong data-testid={`admin-count-${t.key}`}>{(data[t.key] || []).length}</strong>
            <span>{t.label}</span>
          </button>
        ))}
      </div>
      <div className="admin-toolbar">
        <div className="search-box">
          <Search size={15} />
          <input placeholder="Search by name or email" aria-label="Search submissions" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="admin-search-input" />
        </div>
        <div className="filters">
          {[["all", "All"], ["new", "New"], ["contacted", "Contacted"], ["closed", "Closed"]].map(([k, label]) => (
            <button key={k} className={statusFilter === k ? "selected" : ""} onClick={() => setStatusFilter(k)} data-testid={`admin-filter-${k}`}>{label}</button>
          ))}
        </div>
      </div>
      <div className="admin-list" data-testid="admin-submission-list">
        {rows.length === 0 && <p className="empty-note" data-testid="admin-empty-message">No {statusFilter !== "all" || query ? "matching " : ""}{tab} yet.</p>}
        {rows.map((r) => (
          <div className="admin-card" key={r.id} data-testid="admin-submission-card">
            <div className="admin-card-head">
              <div>
                <strong>{r.name}</strong>
                <span className="admin-contact">{r.email}{r.phone ? ` · ${r.phone}` : ""}</span>
              </div>
              <div className="admin-card-meta">
                <span className="admin-date">{fmtDate(r.created_at)}</span>
                <div className="status-row" data-testid="status-row">
                  {STATUSES.map(([k, label]) => (
                    <button key={k} className={`status-chip ${k}${(r.status || "new") === k ? " active" : ""}`} onClick={() => setStatus(tab, r.id, k)} data-testid={`status-${k}-button`}>
                      {label}
                    </button>
                  ))}
                  <button className="icon-btn danger" onClick={() => remove(tab, r)} aria-label={`Delete submission from ${r.name}`} data-testid="submission-delete-button"><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
            <div className="admin-fields">
              {FIELD_MAP[tab].map(([k, label]) => r[k] && <p key={k}><b>{label}:</b> {r[k]}</p>)}
            </div>
            {r.ai_summary && (
              <div className="ai-brief">
                <span className="ai-brief-label"><Sparkles size={13} /> AI brief</span>
                <p>{r.ai_summary}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
